import { Component, ElementRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { APIService, ContentType, ModelContentFilterInput, ModelSortDirection } from '../../API.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ContentGridSelectMode } from './content-grid-select-mode';
import { ContentGridElement } from './content-grid-element';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-content-grid',
  templateUrl: './content-grid.component.html',
  styleUrls: ['./content-grid.component.scss']
})
export class ContentGridComponent implements OnInit {

  @Input() title: string = "Media Library";
  @Input() inset: boolean = false;
  @Input() allowDelete: boolean = false;
  @Input() selectMode: ContentGridSelectMode = ContentGridSelectMode.OFF;
  @Input() selectedContentId: String = null;
  @Input() uploadDialogTitle: String = null;

  @ViewChild('addLinkDialog') addLinkDialogTemplateRef : TemplateRef<any>;
  addLinkDialogRef : MatDialogRef<any>;
  link: String;

  @ViewChild('filterDialog') filterDialogTemplateRef : TemplateRef<any>;

  @ViewChild('filesInput') filesInput: ElementRef;
  
  ContentType = ContentType;
  ContentGridSelectMode = ContentGridSelectMode;

  elements: Map<string, ContentGridElement> = new Map<string, ContentGridElement>();
  sortedElementIds: Array<string> = [];
  loadingElements: boolean;

  filter: ModelContentFilterInput = null;

  sortDirection: ModelSortDirection = ModelSortDirection.DESC;
  
  get sortedElements() : Array<ContentGridElement> {
    return this.sortedElementIds.map(i => this.elements.get(i));
  }

  constructor(
    private api: APIService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private authenticator: AuthenticatorService,
    private currentUser: CurrentUserService
  ) { }

  ngOnInit(): void {
    this.reload();
  }

  reload() {

    this.loadingElements = true;

    this.elements.clear();
    this.sortedElementIds = [];

    this.api.ContentsByUserCreatedAt(this.authenticator.user.attributes.sub, null, this.sortDirection, this.filter).then(contents => {

      contents.items.forEach((content) => {

        var selected = this.selectedContentId == content.id;  
        var element : ContentGridElement = new ContentGridElement(content.type, this.sanitizer, null, content.id, content.value, selected);
        if (element.selected){
          this.selectElement(element);
        }
        element.deleted.subscribe(id => { 
          this.elements.delete(id); 
          this.sortedElementIds.splice(this.sortedElementIds.indexOf(id), 1);
        })

        this.elements.set(element.id, element)
        this.sortedElementIds.push(element.id);

      })

      this.loadingElements = false;
      
    })

  }

  deleteElement(element: ContentGridElement){
    element.delete(this.api);
  }

  selectElement(element: ContentGridElement) {
    if (this.selectMode == ContentGridSelectMode.SINGLE) 
    {
      this.elements.forEach(e => {
        e.selected = false;
      })
    }
    element.selected = true;
  }

  deselectElement(element: ContentGridElement){
    if (this.selectMode == ContentGridSelectMode.SINGLE) {
      return;
    }
    element.selected = false;
  }

  toggleElementSelect(element: ContentGridElement) {
    if (this.selectMode != ContentGridSelectMode.OFF){
      if (element.selected){
        this.deselectElement(element);
      } else {
        this.selectElement(element);
      }
    }
  }

  onFilesChange(event) {

    var files = event.target.files as File[];

    for (var file of files) {

      var validationErrors = [];

      var type = null;
      if (file.type == "video/mp4") {
        type = ContentType.VIDEO;
      } else if (file.type == "image/png") {
        type = ContentType.IMAGE;
      } else if (file.name.endsWith(".gltf") || file.name.endsWith(".zip")) {
        type = ContentType.MODEL;
      } else {
        validationErrors.push("Unsupported file type")
      }

      if (!this.currentUser.isAdmin && file.size > environment.fileUploadSizeLimit_MB * 1048576) {
        validationErrors.push("File too large. " + environment.fileUploadSizeLimit_MB.toString() + "MB Limit Enforced.")
      }

      if (validationErrors.length == 0) {

        var element: ContentGridElement = new ContentGridElement(type, this.sanitizer);
        this.selectElement(element);
        
        element.deleted.subscribe(id => { 
          this.elements.delete(id) 
          this.sortedElementIds.splice(this.sortedElementIds.indexOf(id), 1);
        })

        var institutionId = this.authenticator.user.attributes['custom:institution_id']
        var userId = this.authenticator.user.attributes.sub

        element.setFile(this.api, institutionId, userId, file)

        this.elements.set(element.id, element);
        this.sortedElementIds.unshift(element.id);

     } else {

      alert("There was an issue with the file you attempted to upload: \n" + validationErrors.map(e => "● " + e).join("\n"))

     }
    
    }

    event.target.value = "";

  }

  openAddLinkDialog() {
    this.link = "";
    this.addLinkDialogRef = this.dialog.open(this.addLinkDialogTemplateRef);
  }

  closeAddLinkDialog(){
    this.link = "";
    this.addLinkDialogRef.close();
  }

  cancelAddLink() {
    this.closeAddLinkDialog();
  }

  addLink(){
    this.link.match(/(https?:\/\/)(player.|www.)?(vimeo\.com|youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/);
    var type = null;

    if (RegExp.$3.indexOf('youtu') > -1) {
      type = ContentType.STREAM_YOUTUBE;
    } else if (RegExp.$3.indexOf('vimeo') > -1) {
      type = ContentType.STREAM_VIMEO;
    } else {
      alert('Invalid Link');
      return;
    }

    var videoId = RegExp.$6

    var element = new ContentGridElement(type, this.sanitizer, null, null, videoId);
    this.selectElement(element);   
    element.uploading = true;

    element.deleted.subscribe(id => { 
      this.elements.delete(id) 
      this.sortedElementIds.splice(this.sortedElementIds.indexOf(id), 1);
    })

    this.elements.set(element.id, element)
    this.sortedElementIds.unshift(element.id);

    this.closeAddLinkDialog();

    var institutionId = this.authenticator.user.attributes['custom:institution_id']
    var userId = this.authenticator.user.attributes.sub

    this.api.CreateContent({
      contentInstitutionId: institutionId,
      contentUserId: userId,
      type: type,
      value: videoId
    }).then(createdContent => {
      element.contentId = createdContent.id;
      element.contentValue = createdContent.value;
      element.uploading = false;
    })
  }

  openFilterDialog() {
    this.dialog.open(this.filterDialogTemplateRef);
  }

  applyFilter(contentTypes: Array<ContentType>) {

    this.filter = null;

    this.filter = contentTypes.reduce<ModelContentFilterInput>((prev, curr) => {  
      if (prev == null){
        prev = {}
      }  
      if (prev.or == undefined){
        prev.or = [];
      }
      prev.or.push({
        type: {
          eq: curr
        }
      })
      return prev;
    }, this.filter)

    this.reload();

  }

  toggleSortDirection() {

    if (this.sortDirection == ModelSortDirection.ASC){
      this.sortDirection = ModelSortDirection.DESC;
    } else {
      this.sortDirection = ModelSortDirection.ASC;
    }

    this.reload();

  }

}